// src/pages/Signuppage.jsx

import React, { useState, useEffect, memo, useRef } from "react";
import toast from "react-hot-toast";
import useAuthStore from "../store/useAuthStore";
import useAddressStore from "../store/useAddressStore";
import { useNavigate } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import SuccessPopup from "../components/common/SuccessPopup";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { axiosInstance } from "../lib/axiosInstance";
import GenericModal from "../components/common/GenericModal"; // Import GenericModal
import {
  EyeIcon,
  EyeSlashIcon
} from "@heroicons/react/24/outline";


// Fix for default Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Memoized component for displaying the Mock Aadhaar Card
const MemoizedMockAadhaarCard = memo(({ cardData, aadhaarNumber }) => {
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const placeholderSrc = "/assets/placeholder-avatar.png";

  useEffect(() => {
    setImageLoadFailed(false);
  }, [cardData?.photo_link]);

  if (!cardData) return null;

  // Handle base64 image data
  const aadhaarPhotoSrc = cardData.photo_link
    ? `data:image/jpeg;base64,${cardData.photo_link}`
    : placeholderSrc;

  const currentPhotoToDisplay = imageLoadFailed
    ? placeholderSrc
    : aadhaarPhotoSrc;

  const handleImageError = () => {
    if (!imageLoadFailed && currentPhotoToDisplay === aadhaarPhotoSrc) {
      setImageLoadFailed(true);
    }
  };

  const genderText = (genderCode) => {
    if (genderCode === "M") return "पुरुष / MALE";
    if (genderCode === "F") return "महिला / FEMALE";
    if (genderCode === "T") return "TRANSGENDER"; // Common representation
    return genderCode || "N/A";
  };

  return (
    <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg text-xs font-sans max-w-[300px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 border-b border-gray-300 pb-1.5">
        <img
          src="https://upload.wikimedia.org/wikipedia/en/thumb/c/cf/Aadhaar_Logo.svg/1200px-Aadhaar_Logo.svg.png"
          alt="Aadhaar Logo"
          className="h-7"
        />
        <div className="text-right">
          <p className="text-[9px] font-bold text-gray-700">भारत सरकार</p>
          <p className="text-[8px] text-gray-500">Government of India</p>
        </div>
      </div>

      {/* Tricolor Strip */}
      <div className="flex h-1.5 mb-2.5 rounded-sm overflow-hidden">
        <div className="w-1/3 bg-orange-500"></div>
        <div className="w-1/3 bg-white border-x border-gray-200"></div>
        <div className="w-1/3 bg-green-600"></div>
      </div>

      {/* Content */}
      <div className="flex gap-3 items-start">
        <div className="flex-shrink-0">
          <img
            key={currentPhotoToDisplay}
            src={currentPhotoToDisplay}
            alt="Aadhaar Pic"
            className="w-16 h-20 rounded-md object-cover border-2 border-gray-200 shadow-sm"
            onError={handleImageError}
          />
        </div>
        <div className="flex-grow text-left space-y-1 text-[10px]">
          <p className="font-bold text-sm text-gray-800 leading-tight">
            {cardData.name}
          </p>
          <p className="text-gray-600">
            जन्म तिथि/DOB:{" "}
            <span className="font-semibold text-gray-700">{cardData.dob}</span>
          </p>
          <p className="text-gray-600">
            लिंग/Gender:{" "}
            <span className="font-semibold text-gray-700">
              {genderText(cardData.gender)}
            </span>
          </p>
        </div>
      </div>

      <div className="text-center mt-2.5 mb-1.5">
        <p className="text-xl font-mono tracking-wider text-gray-800 font-bold">
          XXXX XXXX {aadhaarNumber ? aadhaarNumber.slice(-4) : "XXXX"}
        </p>
      </div>

      <div className="mt-2 pt-1.5 border-t border-dashed border-gray-300 text-left">
        <p className="text-[10px] text-gray-500 font-medium">पता/Address:</p>
        <p className="text-[9px] text-gray-700 leading-snug">
          {cardData.address || "N/A"}
        </p>
      </div>

      <div className="mt-3 text-center">
        <span className="inline-flex items-center bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 mr-1.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          AADHAAR VERIFIED
        </span>
      </div>
    </div>
  );
});

const MemoizedMockGstCard = memo(({ cardData }) => {
  if (!cardData) return null;

  return (
    <div className="mt-3 p-3 border border-gray-300 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 shadow-lg text-xs font-sans max-w-[300px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5 border-b border-gray-300 pb-1.5">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/55/Emblem_of_India.svg/1200px-Emblem_of_India.svg.png"
          alt="Emblem of India"
          className="h-8"
        />
        <div className="text-right">
          <p className="text-[10px] font-bold text-gray-700">
            वस्तु एवं सेवा कर
          </p>
          <p className="text-[9px] font-bold text-gray-700">
            Goods and Services Tax
          </p>
          <p className="text-[7px] text-gray-500">Government of India</p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-1.5 text-left text-[10px]">
        <p className="text-gray-600">
          GSTIN:{" "}
          <span className="font-mono font-bold text-gray-800 text-sm">
            {cardData.GSTIN}
          </span>
        </p>
        <p className="text-gray-600">
          Legal Name:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.legal_name_of_business}
          </span>
        </p>
        <p className="text-gray-600">
          Trade Name:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.trade_name_of_business || "N/A"}
          </span>
        </p>
        <p className="text-gray-600">
          Date of Registration:{" "}
          <span className="font-semibold text-gray-700">
            {cardData.date_of_registration}
          </span>
        </p>
        <p className="text-gray-600">
          Status:{" "}
          <span
            className={`font-bold py-0.5 px-1.5 rounded-sm text-[9px] ${cardData.gst_in_status === "Active"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-600"
              }`}
          >
            {cardData.gst_in_status}
          </span>
        </p>
        <div className="pt-1.5 border-t border-dashed border-gray-300 mt-1.5">
          <p className="text-[10px] text-gray-500 font-medium">
            Principal Place of Business:
          </p>
          <p className="text-[9px] text-gray-700 leading-snug">
            {cardData.principal_place_address || "N/A"}
          </p>
        </div>
      </div>
      <div className="mt-3 text-center">
        <span className="inline-flex items-center bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-[10px] font-semibold shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3.5 w-3.5 mr-1.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          GSTIN VERIFIED
        </span>
      </div>
    </div>
  );
});

const DraggableMarker = ({ position, onPositionChange }) => {
  const [currentPosition, setCurrentPosition] = useState(position);
  const map = useMap();

  useEffect(() => {
    setCurrentPosition(position); // Update marker when prop changes
    if (position && position.lat && position.lng) {
      map.setView(
        [position.lat, position.lng],
        map.getZoom() < 13 ? 13 : map.getZoom()
      );
    }
  }, [position, map]);

  useMapEvents({
    click(e) {
      setCurrentPosition(e.latlng);
      if (onPositionChange) onPositionChange(e.latlng);
    },
  });

  return currentPosition && currentPosition.lat && currentPosition.lng ? (
    <Marker
      position={currentPosition}
      draggable={true}
      eventHandlers={{
        dragend: (e) => {
          const newLatLng = e.target.getLatLng();
          setCurrentPosition(newLatLng);
          if (onPositionChange) onPositionChange(newLatLng);
        },
      }}
    />
  ) : null;
};

const SignupPage = () => {
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    companyName: "",
    establishmentYear: "",
    engineerName: "",
    gender: "",
    email: "",
    password: "",
    gst: "",
    aadhaarNumber: "",
    contactNumber: "",
    whatsappNumber: "",
    addressLine1: "",
    addressLine2: "",
    village: "",
    state: "Tamil Nadu",
    district: "",
    taluk: "",
    postcode: "",
    latitude: "",
    longitude: "",
    qualification: "",
    governmentLicense: null,
    graduationCertificate: null,
    profileImage: null,
    agreementConfirmed: false,
    privacyPolicyConfirmed: false,
    warrantyConfirmed: false,
    aadhaarOtp: "",
    aadhaarRefId: "",
    aadhaarVerified: false,
    gstVerified: false,
    verifiedAadhaarData: null,
    verifiedGstinData: null,
    selectedServiceAreas: [],
    emailOtp: "",
    emailVerified: false,
    qualificationCustom: "", // Add custom qualification field
  });

  // --- MODIFICATION START: Added attemptedNext state ---
  const [attemptedNext, setAttemptedNext] = useState(false);
  // --- MODIFICATION END ---

  // --- PERSISTENCE LOGIC START ---
  const [fileMetadata, setFileMetadata] = useState({
    governmentLicense: null,
    graduationCertificate: null,
    profileImage: null,
  });

  useEffect(() => {
    const savedData = localStorage.getItem("builderSignupData");
    if (savedData) {
      const { formData: savedForm, timestamp, step, fileMetadata: savedFileMetadata } = JSON.parse(savedData);
      const oneHour = 60 * 60 * 1000;
      if (Date.now() - timestamp < oneHour) {
        setFormData(savedForm);
        setCurrentStep(step || 0);
        if (savedFileMetadata) {
          setFileMetadata(savedFileMetadata);
        }
      } else {
        localStorage.removeItem("builderSignupData");
      }
    }
  }, []);

  useEffect(() => {
    // Create a copy of formData without File objects for localStorage
    const formDataToSave = { ...formData };
    delete formDataToSave.governmentLicense;
    delete formDataToSave.graduationCertificate;
    delete formDataToSave.profileImage;
    
    const dataToSave = {
      formData: formDataToSave,
      fileMetadata,
      timestamp: Date.now(),
      step: currentStep,
    };
    
    try {
      localStorage.setItem("builderSignupData", JSON.stringify(dataToSave));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('LocalStorage quota exceeded. Form data not saved.');
      }
    }
  }, [formData, currentStep, fileMetadata]);
  // --- PERSISTENCE LOGIC END ---

  const { signupBuilder, isSigningUp } = useAuthStore();
  const {
    districts,
    taluks,
    fetchMappedDistricts,
    fetchMappedTaluks,
    isLoadingDistricts,
    isLoadingTaluks,
  } = useAddressStore();
  const navigate = useNavigate();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showCustomQualificationInput, setShowCustomQualificationInput] = useState(false);

  const [isVerifyingAadhaar, setIsVerifyingAadhaar] = useState(false);
  const [isVerifyingAadhaarOtp, setIsVerifyingAadhaarOtp] = useState(false);
  const [isVerifyingGst, setIsVerifyingGst] = useState(false);
  const [showAadhaarOtpInput, setShowAadhaarOtpInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [isVerifyingEmailOtp, setIsVerifyingEmailOtp] = useState(false);
  const [showEmailOtpInput, setShowEmailOtpInput] = useState(false);

  const [aadhaarCardData, setAadhaarCardData] = useState(null);
  const [gstCardData, setGstCardData] = useState(null);

  const [serviceAreas, setServiceAreas] = useState([]);
  const [isLoadingServiceAreas, setIsLoadingServiceAreas] = useState(false);

  // --- MODIFICATION START ---
  const [noGst, setNoGst] = useState(false);

  const handleNoGstChange = (e) => {
    const isChecked = e.target.checked;
    setNoGst(isChecked);
    if (isChecked) {
      setFormData((prev) => ({
        ...prev,
        gst: "",
        gstVerified: false,
        verifiedGstinData: null,
      }));
      setGstCardData(null);
    }
  };

  const [noGovernmentLicense, setNoGovernmentLicense] = useState(false);

  const handleNoGovernmentLicenseChange = (e) => {
    const isChecked = e.target.checked;
    setNoGovernmentLicense(isChecked);
    if (isChecked) {
      setFormData((prev) => ({
        ...prev,
        governmentLicense: null,
      }));
      setFileMetadata((prev) => ({
        ...prev,
        governmentLicense: null,
      }));
    }
  };
  // --- MODIFICATION END ---

  useEffect(() => {
    fetchMappedDistricts();

    const fetchAreas = async () => {
      setIsLoadingServiceAreas(true);
      try {
        const { axiosInstancev1 } = await import("../lib/axiosInstance");
        const response = await axiosInstancev1.get(
          "/address/service-area-options?state=Tamil%20Nadu"
        );
        if (response.data.success && Array.isArray(response.data.data)) {
          setServiceAreas(response.data.data);
        } else {
          toast.error("Failed to load service areas configuration.");
          setServiceAreas([]);
        }
      } catch (error) {
        toast.error("Error fetching service areas configuration.");
        console.error("Service Area Fetch Error:", error);
        setServiceAreas([]);
      } finally {
        setIsLoadingServiceAreas(false);
      }
    };
    fetchAreas();
  }, [fetchMappedDistricts]);

  const previousDistrictRef = useRef(null);

  useEffect(() => {
    if (formData.district) {
      fetchMappedTaluks(formData.district);
      
      // Only reset taluk if district actually changed from a previous value
      if (previousDistrictRef.current !== null && 
          previousDistrictRef.current !== formData.district) {
        setFormData((prev) => ({ ...prev, taluk: "" }));
      }
      
      // Update the ref with current district
      previousDistrictRef.current = formData.district;
    }
  }, [formData.district, fetchMappedTaluks]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleMapInteraction = (latlng) => {
    setFormData((prev) => ({
      ...prev,
      latitude: latlng.lat.toFixed(6).toString(),
      longitude: latlng.lng.toFixed(6).toString(),
    }));
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData((prev) => ({
            ...prev,
            latitude: latitude.toFixed(6).toString(),
            longitude: longitude.toFixed(6).toString(),
          }));
          toast.success("Location detected successfully!");
        },
        (error) => {
          console.error("Error getting location:", error);
          toast.error(
            "Unable to get your location. Please click on the map or enter manually."
          );
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 } // Optimized settings
      );
    } else {
      toast.error(
        "Geolocation is not supported. Please click on the map or enter manually."
      );
    }
  };

  const verifyAadhaar = async () => {
    if (
      !formData.aadhaarNumber ||
      formData.aadhaarNumber.replace(/\s/g, "").length !== 12
    ) {
      toast.error("Please enter a valid 12-digit Aadhaar number.");
      return;
    }
    setIsVerifyingAadhaar(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/aadhaar/generate-otp",
        {
          aadhaarNumber: formData.aadhaarNumber.replace(/\s/g, ""),
        }
      );
      if (response.data.ref_id) {
        setFormData({ ...formData, aadhaarRefId: response.data.ref_id });
        setShowAadhaarOtpInput(true);
        toast.success(response.data.message || "OTP sent successfully.");
      } else {
        toast.error(response.data.message || "Failed to send Aadhaar OTP.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Aadhaar verification request failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingAadhaar(false);
    }
  };

  const verifyAadhaarOTP = async () => {
    if (!formData.aadhaarOtp || formData.aadhaarOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }
    setIsVerifyingAadhaarOtp(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/aadhaar/verify-otp",
        {
          otp: formData.aadhaarOtp,
          ref_id: formData.aadhaarRefId,
          aadhaarNumber: formData.aadhaarNumber.replace(/\s/g, ""),
        }
      );
      if (
        response.data.message === "Aadhaar verified successfully." &&
        response.data.details
      ) {
        const details = response.data.details;
        setFormData((prev) => ({
          ...prev,
          aadhaarVerified: true,
          engineerName: details.name || prev.engineerName,
          gender:
            details.gender === "M"
              ? "Male"
              : details.gender === "F"
                ? "Female"
                : details.gender === "T"
                  ? "Transgender"
                  : prev.gender,
          verifiedAadhaarData: details,
        }));
        setAadhaarCardData(details);
        setShowAadhaarOtpInput(false);
        toast.success("Aadhaar verified successfully!");
      } else {
        toast.error(
          response.data.message || "Invalid OTP or verification failed."
        );
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "Aadhaar OTP verification failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingAadhaarOtp(false);
    }
  };

  const verifyGST = async () => {
    if (!formData.gst || formData.gst.length !== 15) {
      toast.error("Please enter a valid 15-character GSTIN.");
      return;
    }
    setIsVerifyingGst(true);
    try {
      const response = await axiosInstance.post("/builders/auth/gstin/verify", {
        gstin: formData.gst,
      });
      if (
        response.data.message === "GSTIN verified successfully." &&
        response.data.details
      ) {
        const details = response.data.details;
        setFormData((prev) => ({
          ...prev,
          gstVerified: true,
          companyName: details.legal_name_of_business || prev.companyName,
          verifiedGstinData: details,
        }));
        setGstCardData(details);
        toast.success("GSTIN verified successfully!");
      } else {
        toast.error(response.data.message || "GSTIN verification failed.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "GSTIN verification request failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingGst(false);
    }
  };

  const sendEmailOtp = async () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setIsVerifyingEmail(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/send-email-otp",
        { email: formData.email }
      );
      if (response.data.success) {
        setShowEmailOtpInput(true);
        toast.success(response.data.message || "OTP sent successfully!");
      } else {
        toast.error(response.data.message || "Failed to send OTP.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message ||
        "An account with this email already exists.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!formData.emailOtp || formData.emailOtp.length !== 6) {
      toast.error("Please enter the 6-digit OTP.");
      return;
    }
    setIsVerifyingEmailOtp(true);
    try {
      const response = await axiosInstance.post(
        "/builders/auth/verify-email-otp",
        { email: formData.email, otp: formData.emailOtp }
      );
      if (response.data.success) {
        toast.success(response.data.message || "Email verified successfully!");
        setFormData((prev) => ({ ...prev, emailVerified: true }));
        setShowEmailOtpInput(false);
      } else {
        toast.error(response.data.message || "Invalid OTP.");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || "OTP verification failed.";
      toast.error(errorMsg);
    } finally {
      setIsVerifyingEmailOtp(false);
    }
  };

  const handleSameAsPhone = () => {
    setFormData((prev) => ({
      ...prev,
      whatsappNumber: prev.contactNumber,
    }));
  };

  const handleInputChange = (e, id) => {
    const { value, type } = e.target;
    let processedValue = value;

    if (
      type === "number" ||
      [
        "establishmentYear",
        "postcode",
        "contactNumber",
        "whatsappNumber",
        "aadhaarOtp",
        "emailOtp",
      ].includes(id)
    ) {
      processedValue = value.replace(/\D/g, "");
    }
    if (id === "aadhaarNumber") {
      let rawValue = value.replace(/\D/g, "").substring(0, 12);
      let formattedValue = "";
      for (let i = 0; i < rawValue.length; i++) {
        if (i > 0 && i % 4 === 0) {
          formattedValue += " ";
        }
        formattedValue += rawValue[i];
      }
      e.target.value = formattedValue;
      processedValue = rawValue;

      if (formData.aadhaarVerified && rawValue !== formData.aadhaarNumber) {
        setFormData((prev) => ({
          ...prev,
          aadhaarVerified: false,
          verifiedAadhaarData: null,
          aadhaarRefId: "",
          aadhaarOtp: "",
          engineerName: "", // Clear auto-filled fields
          gender: "",
        }));
        setShowAadhaarOtpInput(false);
        setAadhaarCardData(null);
        toast("Aadhaar number changed. Please re-verify.", { icon: "ℹ️" });
      }
    } else if (id === "gst") {
      // --- MODIFICATION START ---
      // If user starts typing in GST field, uncheck the "no GST" box
      setNoGst(false);
      // --- MODIFICATION END ---
      processedValue = value
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .substring(0, 15);
      e.target.value = processedValue;

      if (formData.gstVerified && processedValue !== formData.gst) {
        setFormData((prev) => ({
          ...prev,
          gstVerified: false,
          verifiedGstinData: null,
          companyName: "", // Clear auto-filled field
        }));
        setGstCardData(null);
        toast("GSTIN changed. Please re-verify.", { icon: "ℹ️" });
      }
    }

    if (id === "establishmentYear" && processedValue.length > 4)
      processedValue = processedValue.slice(0, 4);
    if (id === "postcode" && processedValue.length > 6)
      processedValue = processedValue.slice(0, 6);
    if (
      (id === "contactNumber" || id === "whatsappNumber") &&
      processedValue.length > 10
    )
      processedValue = processedValue.slice(0, 10);
    if ((id === "aadhaarOtp" || id === "emailOtp") && processedValue.length > 6)
      processedValue = processedValue.slice(0, 6);

    if (id === "latitude" || id === "longitude") {
      processedValue = value.replace(/[^\d.-]/g, "");
    }

    // --- MODIFICATION START: Capitalize first letter for text fields ---
    const excludedIds = [
      "email", "password", "gst", "aadhaarNumber", "contactNumber",
      "whatsappNumber", "postcode", "establishmentYear", "aadhaarOtp",
      "emailOtp", "latitude", "longitude"
    ];

    if (type === "text" && !excludedIds.includes(id) && processedValue.length > 0) {
      processedValue = processedValue.charAt(0).toUpperCase() + processedValue.slice(1);
    }
    // --- MODIFICATION END ---

    setFormData((prev) => ({ ...prev, [id]: processedValue }));
  };

  const handleFileChange = (e, id) => {
    const file = e.target.files[0];
    if (file) {
      // If user uploads government license, uncheck the "no government license" box
      if (id === "governmentLicense") {
        setNoGovernmentLicense(false);
      }

      const fieldLabel =
        steps[currentStep].fields.find((f) => f.id === id)?.label || "File";
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type for ${fieldLabel}. PDF, JPG, PNG only.`);
        e.target.value = null;
        return;
      }
      if (file.size > maxSize) {
        toast.error(`File size for ${fieldLabel} exceeds 5MB.`);
        e.target.value = null;
        return;
      }
      setFormData({ ...formData, [id]: file });
      setFileMetadata(prev => ({
        ...prev,
        [id]: { name: file.name, size: file.size, type: file.type }
      }));
    }
  };

  const handleprofileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];
      const maxSize = 2 * 1024 * 1024;
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid profile image type. JPG, PNG, WEBP only.");
        e.target.value = null;
        return;
      }
      if (file.size > maxSize) {
        toast.error("Profile image size exceeds 2MB.");
        e.target.value = null;
        return;
      }
      setProfileImagePreview(URL.createObjectURL(file));
      setFormData({ ...formData, profileImage: file });
      setFileMetadata(prev => ({
        ...prev,
        profileImage: { name: file.name, size: file.size, type: file.type }
      }));
    }
  };

  const handleCheckboxChange = (id) => {
    setFormData({ ...formData, [id]: !formData[id] });
  };

  const validateStep = () => {
    const stepFields = steps[currentStep].fields;
    for (let field of stepFields) {
      // --- MODIFICATION START ---
      // If it's the GST field and the "no GST" box is checked, skip validation for it.
      if (field.id === "gst" && noGst) {
        continue;
      }
      // If it's the Government License field and the "no Government License" box is checked, skip validation for it.
      if (field.id === "governmentLicense" && noGovernmentLicense) {
        continue;
      }
      // --- MODIFICATION END ---
      if (
        field.required &&
        (!formData[field.id] ||
          (typeof formData[field.id] === "string" &&
            formData[field.id].trim() === ""))
      ) {
        if (field.id === "aadhaarOtp" && !showAadhaarOtpInput) continue;
        if (field.id === "emailOtp" && !showEmailOtpInput) continue;
        toast.error(`Please fill out the '${field.label}' field.`);
        return false;
      }
      // ... (other existing validations for password, year, etc.)
    }

    // Step-specific verification checks
    if (currentStep === 0) {
      // --- MODIFICATION START ---
      // Only check for GST verification if the "no GST" box is NOT checked.
      if (!noGst && !formData.gstVerified) {
        toast.error("Please verify your GSTIN before proceeding.");
        return false;
      }
      // --- MODIFICATION END ---
      if (!formData.aadhaarVerified) {
        toast.error("Please verify your Aadhaar number before proceeding.");
        return false;
      }
      if (!formData.emailVerified) {
        toast.error("Please verify your email address before proceeding.");
        return false;
      }
    }

    // Note: Agreement validation moved to handleSubmit only
    return true;
  };

  const handleNextStep = () => {
    if (!validateStep()) return;
    if (currentStep < steps.length - 1) setCurrentStep((prev) => prev + 1);
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    // If not on the last step, move to next step instead of submitting
    if (currentStep < steps.length - 1) {
      handleNextStep();
      return;
    }

    // Validate agreements only on submit
    if (!formData.agreementConfirmed) {
      return;
    }
    if (!formData.privacyPolicyConfirmed) {
      toast.error("Please agree to the Privacy Policy.");
      return;
    }
    if (!formData.warrantyConfirmed) {
      toast.error("Please agree to provide warranty service.");
      return;
    }
    if (!formData.profileImage) {
      toast.error("Please upload a profile image.");
      return;
    }

    if (isLoadingServiceAreas) {
      toast.error("Service area configuration is still loading. Please wait.");
      return;
    }

    if (!serviceAreas || serviceAreas.length === 0) {
      toast.error(
        "Service area list is not available. Cannot complete signup."
      );
      return;
    }

    const matchingServiceArea = serviceAreas.find(
      (area) =>
        area.district === formData.district && area.taluk === formData.taluk
    );

    if (!matchingServiceArea) {
      toast.error(
        `The selected address (${formData.taluk}, ${formData.district}) is not a serviceable area.`
      );
      return;
    }

    const finalFormData = {
      ...formData,
      selectedServiceAreas: [matchingServiceArea.id],
      qualification:
        formData.qualification === "Others"
          ? formData.qualificationCustom
          : formData.qualification,
    };

    try {
      const result = await signupBuilder(finalFormData);
      if (result.success) {
        localStorage.removeItem("builderSignupData");
        setPopupMessage(
          "Your details are being processed. You can log in once the verification is complete"
        );
        setIsPopupOpen(true);
      }
    } catch (error) {
      console.error("Signup failed:", error);
    }
  };

  const closePopupAndRedirect = () => {
    setIsPopupOpen(false);
    navigate("/login?type=builder");
  };

  const mapCenter =
    formData.latitude &&
      formData.longitude &&
      !isNaN(parseFloat(formData.latitude)) &&
      !isNaN(parseFloat(formData.longitude))
      ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
      : [20.5937, 78.9629];

  const markerPos =
    formData.latitude &&
      formData.longitude &&
      !isNaN(parseFloat(formData.latitude)) &&
      !isNaN(parseFloat(formData.longitude))
      ? {
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude),
      }
      : null;

  const steps = [
    {
      title: "Builders Info",
      fields: [
        {
          label: "GSTIN",
          type: "text",
          id: "gst",
          maxLength: 15,
          required: true,
          verifyButton: true,
          verifyFunction: verifyGST,
          isLoading: isVerifyingGst,
          isVerified: formData.gstVerified,
          // --- MODIFICATION START ---
          checkbox: {
            id: "noGstCheckbox",
            label: "I don't have a GSTIN",
            checked: noGst,
            onChange: handleNoGstChange,
          },
          // --- MODIFICATION END ---
        },
        {
          label: "Aadhaar Number",
          type: "text",
          id: "aadhaarNumber",
          maxLength: 14,
          required: true,
          verifyButton: true,
          verifyFunction: verifyAadhaar,
          isLoading: isVerifyingAadhaar,
          isVerified: formData.aadhaarVerified,
          inputMode: "numeric",
        },
        {
          label: "Aadhaar OTP",
          type: "text",
          id: "aadhaarOtp",
          maxLength: 6,
          required: true,
          verifyButton: true,
          verifyFunction: verifyAadhaarOTP,
          isLoading: isVerifyingAadhaarOtp,
          showConditionally: showAadhaarOtpInput && !formData.aadhaarVerified,
          inputMode: "numeric",
        },
        {
          label: "Email",
          type: "email",
          id: "email",
          required: true,
          disabled: formData.emailVerified || showEmailOtpInput,
          verifyButton: true,
          verifyFunction: sendEmailOtp,
          isLoading: isVerifyingEmail,
          isVerified: formData.emailVerified,
          showVerifyButtonOnlyWhen: !showEmailOtpInput,
        },
        {
          label: "Email OTP",
          type: "text",
          id: "emailOtp",
          maxLength: 6,
          required: true,
          showConditionally: showEmailOtpInput && !formData.emailVerified,
          verifyButton: true,
          verifyFunction: verifyEmailOtp,
          isLoading: isVerifyingEmailOtp,
          inputMode: "numeric",
        },
        {
          label: "Company Name",
          type: "text",
          id: "companyName",
          required: true,
          disabled: formData.gstVerified,
        },
        {
          label: "Establishment Year",
          type: "number",
          id: "establishmentYear",
          required: true,
          min: 1900,
          max: new Date().getFullYear(),
          inputMode: "numeric",
        },
        {
          label: "Engineer Name",
          type: "text",
          id: "engineerName",
          required: true,
          disabled: formData.aadhaarVerified,
        },
        {
          label: "Gender",
          type: "select",
          id: "gender",
          options: ["", "Male", "Female", "Transgender", "Other"],
          required: true,
          disabled: formData.aadhaarVerified,
        },
        {
          label: "Create Password",
          type: "password",
          id: "password",
          required: true,
          minLength: 8,
        },
        {
          label: "Contact Number",
          type: "tel",
          id: "contactNumber",
          required: true,
          maxLength: 10,
          inputMode: "numeric",
        },
        {
          label: "WhatsApp Number",
          type: "tel",
          id: "whatsappNumber",
          maxLength: 10,
          button: { text: "Same as Phone", onClick: handleSameAsPhone },
          inputMode: "numeric",
        },
      ],
    },
    {
      title: "Address",
      fields: [
        {
          label: "Address Line 1",
          type: "text",
          id: "addressLine1",
          required: true,
        },
        { label: "Address Line 2", type: "text", id: "addressLine2" },
        { label: "Village/Town", type: "text", id: "village", required: true },
        {
          label: "District",
          type: "select-address",
          id: "district",
          optionsSource: districts,
          isLoadingSource: isLoadingDistricts,
          required: true,
        },
        {
          label: "Taluk",
          type: "select-address",
          id: "taluk",
          optionsSource: taluks,
          isLoadingSource: isLoadingTaluks,
          disabled: !formData.district,
          required: true,
        },
        {
          label: "Postcode",
          type: "text",
          id: "postcode",
          required: true,
          maxLength: 6,
          inputMode: "numeric",
        },
        {
          label: "Latitude",
          type: "text",
          id: "latitude",
          required: true,
          readOnly: true,
        },
        {
          label: "Longitude",
          type: "text",
          id: "longitude",
          required: true,
          readOnly: true,
        },
      ],
    },
    {
      title: "Document",
      fields: [
        {
          label: "Qualification",
          type: "select",
          id: "qualification",
          options: [
            "",
            "BE - CIVIL",
            "DIPLOMA (CIVIL)",
            "ME - CIVIL",
            "BE - ARCHITECTURAL",
            "ME - ARCHITECTURAL",
            "Others",
          ],
          required: true,
          colSpan: "md:col-span-2",
        },
        {
          label: "Government License (PDF, JPG, PNG - Max 5MB)",
          type: "file",
          id: "governmentLicense",
          required: true,
          accept: ".pdf,.jpg,.jpeg,.png",
          checkbox: {
            id: "noGovernmentLicenseCheckbox",
            label: "I don't have a Government License",
            checked: noGovernmentLicense,
            onChange: handleNoGovernmentLicenseChange,
          },
        },
        {
          label: "Graduation Certificate (PDF, JPG, PNG - Max 5MB)",
          type: "file",
          id: "graduationCertificate",
          required: true,
          accept: ".pdf,.jpg,.jpeg,.png",
        },
      ],
    },
    {
      title: "Agreements",
      fields: [],
    },
  ];

  const isAddressStep = currentStep === 1;
  const isDocumentStep = currentStep === 2;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: "url('/assets/Profile Bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <SuccessPopup
        isOpen={isPopupOpen}
        onClose={closePopupAndRedirect}
        message={popupMessage}
      />
      <div className="w-full max-w-4xl p-6 border bg-white rounded-[40px] shadow-lg">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-700 ">
            BUILDER REGISTRATION{" "}
          </h2>
          <p className="text-lg font-medium text-black mt-2">
            <span className="text-black font-poppins font-bold">
              {" "}
              Join Ducktail
            </span>{" "}
            -{" "}
            <span className="text-blue-500 font-poppins font-bold">
              {" "}
              Get more Projects
            </span>
          </p>
          <p className="text-sm text-gray-500">
            Connect with real clients looking for certified builders nearby
          </p>
        </div>
        <div className="mb-6">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col items-center flex-1 relative"
              >
                {index > 0 && (
                  <div
                    className="absolute w-full h-0.5 top-4 -left-1/2 z-0"
                    style={{
                      backgroundColor:
                        index <= currentStep ? "#3B82F6" : "#E5E7EB",
                    }}
                  ></div>
                )}
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-medium transition-colors duration-300 z-10 ${index < currentStep
                    ? "bg-green-500"
                    : index === currentStep
                      ? "bg-blue-500"
                      : "bg-gray-300"
                    }`}
                >
                  {index < currentStep ? "✓" : index + 1}
                </div>
                <span
                  className={`mt-2 text-[10px] font-medium text-center ${index === currentStep ? "text-blue-500" : "text-gray-500"
                    }`}
                >
                  {step.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6">
          {isAddressStep && (
            <div className="mb-6 border-b pb-6">
              <label className="text-gray-700 text-sm font-medium mb-2 block">
                Select Your Office Location on Map{" "}
                <span className="text-red-500">*</span>
              </label>
              <MapContainer
                key={mapCenter.join("-")}
                center={mapCenter}
                zoom={markerPos ? 13 : 5}
                style={{ height: "200px", width: "100%" }}
                className="border rounded-md overflow-hidden mb-3 shadow-sm"
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <DraggableMarker
                  position={markerPos}
                  onPositionChange={handleMapInteraction}
                />
              </MapContainer>
              <p className="text-xs text-gray-500 mb-3 text-center">
                Click on map or drag marker to set location, or use button
                below.
              </p>
              <div className="flex justify-center">
                <button
                  type="button"
                  className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center text-sm hover:bg-blue-600 transition-colors"
                  onClick={getCurrentLocation}
                  disabled={isSigningUp}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Get Current Location
                </button>
              </div>
            </div>
          )}

          <div
            className={`grid grid-cols-1 ${isDocumentStep ? "md:grid-cols-2" : "md:grid-cols-3"
              } gap-x-6 gap-y-4`}
          >
            {steps[currentStep].fields.map((field) => {
              if (field.showConditionally === false) return null;
              const isLatLngField =
                field.id === "latitude" || field.id === "longitude";

              // --- MODIFICATION START: Validation Logic ---
              const isRequired = field.required && (field.id === "gst" ? !noGst : field.id === "governmentLicense" ? !noGovernmentLicense : true);
              const isEmpty = !formData[field.id] || (typeof formData[field.id] === "string" && formData[field.id].trim() === "");

              // Special check for OTP fields visibility
              if (field.id === "aadhaarOtp" && !showAadhaarOtpInput && isEmpty) { /* don't count as error if hidden */ }
              else if (field.id === "emailOtp" && !showEmailOtpInput && isEmpty) { /* don't count as error if hidden */ }

              const hasError = attemptedNext && isRequired && isEmpty;
              // --- MODIFICATION END ---

              return (
                <div key={field.id} className={`mb-1 ${field.colSpan || ""}`}>
                  <label
                    htmlFor={field.id}
                    className="text-gray-900 text-xs font-medium mb-1 block"
                  >
                    {field.label}
                    {field.required && (field.id === "gst" ? !noGst : field.id === "governmentLicense" ? !noGovernmentLicense : true) && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <div className="flex items-center">
                    {field.type === "file" ? (
                      <div className="w-full">
                        {formData[field.id] instanceof File ? (
                          <div className="relative">
                            <div className="w-full p-2 border border-green-500 bg-green-50 rounded-lg flex items-center justify-between">
                              <div className="flex items-center flex-1 min-w-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-green-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs text-green-700 font-medium truncate">{formData[field.id].name}</span>
                              </div>
                              <label
                                htmlFor={field.id}
                                className="ml-2 px-3 py-1 bg-blue-500 text-white rounded-md text-xs cursor-pointer hover:bg-blue-600 transition-colors flex-shrink-0"
                              >
                                Change
                              </label>
                            </div>
                            <input
                              type="file"
                              id={field.id}
                              name={field.id}
                              accept={field.accept}
                              onChange={(e) => handleFileChange(e, field.id)}
                              className="hidden"
                              disabled={isSigningUp || field.disabled || (field.id === "governmentLicense" && noGovernmentLicense)}
                            />
                          </div>
                        ) : fileMetadata[field.id] ? (
                          <div className="relative">
                            <div className="w-full p-2 border border-amber-500 bg-amber-50 rounded-lg flex items-center justify-between">
                              <div className="flex items-center flex-1 min-w-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-amber-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="text-xs text-amber-700 font-medium truncate">Re-upload: {fileMetadata[field.id].name}</span>
                              </div>
                              <label
                                htmlFor={field.id}
                                className="ml-2 px-3 py-1 bg-blue-500 text-white rounded-md text-xs cursor-pointer hover:bg-blue-600 transition-colors flex-shrink-0"
                              >
                                Upload
                              </label>
                            </div>
                            <input
                              type="file"
                              id={field.id}
                              name={field.id}
                              accept={field.accept}
                              onChange={(e) => handleFileChange(e, field.id)}
                              className="hidden"
                              disabled={isSigningUp || field.disabled || (field.id === "governmentLicense" && noGovernmentLicense)}
                            />
                          </div>
                        ) : (
                          <input
                            type="file"
                            id={field.id}
                            name={field.id}
                            accept={field.accept}
                            onChange={(e) => handleFileChange(e, field.id)}
                            className={`w-full p-2 border ${hasError ? "border-red-500" : "border-gray-400"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 text-xs`}
                            disabled={isSigningUp || field.disabled || (field.id === "governmentLicense" && noGovernmentLicense)}
                          />
                        )}
                      </div>
                    ) : field.type === "select" ? (
                      <div className="w-full">
                        <select
                          id={field.id}
                          value={formData[field.id] || ""}
                          onChange={(e) => {
                            if (field.id === "qualification" && e.target.value === "Others") {
                              setShowCustomQualificationInput(true);
                              setFormData((prev) => ({ ...prev, qualification: "Others" }));
                            } else if (field.id === "qualification") {
                              setShowCustomQualificationInput(false);
                              handleInputChange(e, field.id);
                            } else {
                              handleInputChange(e, field.id);
                            }
                          }}
                          className={`w-full p-2 border ${hasError ? "border-red-500" : "border-gray-700"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs`}
                          required={field.required}
                          disabled={isSigningUp || field.disabled}
                        >
                          {field.options.map((option) => (
                            <option key={option} value={option}>
                              {option === "" ? "Select..." : option}
                            </option>
                          ))}
                        </select>
                        {field.id === "qualification" && showCustomQualificationInput && (
                          <input
                            type="text"
                            placeholder="Enter your qualification"
                            value={formData.qualificationCustom || ""}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                qualificationCustom: e.target.value,
                              }))
                            }
                            className="mt-2 w-full p-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs bg-blue-50"
                            required
                          />
                        )}
                      </div>
                    ) : field.type === "select-address" ? (
                      <select
                        id={field.id}
                        value={formData[field.id] || ""}
                        onChange={(e) => handleInputChange(e, field.id)}
                        className={`w-full p-2 border ${hasError ? "border-red-500" : "border-gray-700"} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs`}
                        required={field.required}
                        disabled={
                          isSigningUp || field.disabled || field.isLoadingSource
                        }
                      >
                        <option value="">
                          {field.isLoadingSource
                            ? "Loading..."
                            : `Select ${field.label}`}
                        </option>
                        {field.optionsSource &&
                          Array.isArray(field.optionsSource) &&
                          field.optionsSource.map((optionValue) => (
                            <option key={optionValue} value={optionValue}>
                              {optionValue}
                            </option>
                          ))}
                      </select>
                    ) : (
                      <div className="relative w-full flex items-center">
                        <input
                          type={
                            field.id === "password" && !showPassword
                              ? "password"
                              : "text"
                          }
                          id={field.id}
                          value={formData[field.id] || ""}
                          {...(!isLatLngField && {
                            onChange: (e) => handleInputChange(e, field.id),
                          })}
                          {...(field.inputMode && { inputMode: field.inputMode })}
                          {...(field.inputMode === "numeric" && { pattern: "[0-9]*" })}
                          className={`w-full p-2 border ${hasError ? "border-red-500" : "border-gray-700"} rounded-[12px] focus:outline-none focus:ring-2 focus:ring-blue-400 text-xs ${field.disabled || (field.id === "gst" && noGst)
                            ? "bg-gray-100"
                            : ""
                            } ${field.readOnly
                              ? "bg-gray-100 cursor-not-allowed"
                              : ""
                            }`}
                          placeholder={
                            isLatLngField
                              ? "Auto-filled from map"
                              : `Enter ${field.label}`
                          }
                          required={
                            field.required &&
                            (field.id === "gst" ? !noGst : true)
                          }
                          min={field.min}
                          max={field.max}
                          maxLength={field.maxLength}
                          readOnly={field.readOnly}
                          disabled={
                            isSigningUp ||
                            field.disabled ||
                            (field.id === "gst" && noGst)
                          }
                          tabIndex={isLatLngField ? -1 : undefined}
                        />
                        {field.id === "password" && (
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-4 w-4" />
                            ) : (
                              <EyeIcon className="h-4 w-4" />
                            )}
                          </button>
                        )}
                        {field.button && (
                          <button
                            type="button"
                            onClick={field.button.onClick}
                            className="ml-2 px-2 py-1.5 whitespace-nowrap rounded-lg text-[10px] bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                            disabled={isSigningUp || field.disabled}
                          >
                            {field.button.text}
                          </button>
                        )}
                      </div>
                    )}
                    {field.verifyButton &&
                      (field.showVerifyButtonOnlyWhen === undefined ||
                        field.showVerifyButtonOnlyWhen) && (
                        <button
                          type="button"
                          onClick={field.verifyFunction}
                          className={`ml-2 px-3 py-2 rounded-lg text-xs flex items-center justify-center min-w-[60px] ${field.isVerified
                            ? "bg-green-500 text-white cursor-not-allowed"
                            : "bg-blue-500 text-white hover:bg-blue-600"
                            } transition-colors`}
                          disabled={
                            isSigningUp ||
                            !formData[field.id] ||
                            field.isLoading ||
                            field.isVerified ||
                            (field.id === "gst" && noGst)
                          }
                        >
                          {field.isLoading ? (
                            <AiOutlineLoading3Quarters className="animate-spin" />
                          ) : field.isVerified ? (
                            "Verified ✓"
                          ) : (
                            "Verify"
                          )}
                        </button>
                      )}
                  </div>
                  {field.checkbox && (
                    <div className="mt-1.5 flex items-center">
                      <input
                        type="checkbox"
                        id={field.checkbox.id}
                        checked={field.checkbox.checked}
                        onChange={field.checkbox.onChange}
                        className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        disabled={isSigningUp}
                      />
                      <label
                        htmlFor={field.checkbox.id}
                        className="ml-1.5 text-[10px] text-gray-600"
                      >
                        {field.checkbox.label}
                      </label>
                    </div>
                  )}
                  {field.id === "aadhaarNumber" &&
                    formData.aadhaarVerified &&
                    aadhaarCardData && (
                      <MemoizedMockAadhaarCard
                        cardData={aadhaarCardData}
                        aadhaarNumber={formData.aadhaarNumber}
                      />
                    )}
                  {field.id === "gst" &&
                    formData.gstVerified &&
                    gstCardData && (
                      <MemoizedMockGstCard cardData={gstCardData} />
                    )}
                </div>
              );
            })}
          </div>

          {currentStep === steps.length - 1 && (
            <>
              <div className="col-span-full mb-3 flex flex-col items-center">
                <label className="text-gray-700 text-xs font-medium mb-1 block text-center">
                  Company Logo <span className="text-red-500">*</span>
                </label>
                <div className="w-24 h-24 border-2 border-gray-400 border-dashed rounded-full flex items-center justify-center overflow-hidden mb-1 relative">
                  {profileImagePreview ? (
                    <img
                      src={profileImagePreview}
                      alt="Profile Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      ></path>
                    </svg>
                  )}
                  <input
                    type="file"
                    id="profileImage"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleprofileImageChange}
                    className="opacity-0 absolute inset-0 cursor-pointer"
                    disabled={isSigningUp}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Click to upload (JPG, PNG, WEBP - Max 2MB)
                </p>
              </div>
              <div className="col-span-full">
                <div className="space-y-2 mb-4">
                  {[
                    {
                      id: "agreementConfirmed",
                      label: "I agree to the",
                      link: "/termscondition",
                      linkText: "Terms and Conditions",
                    },
                    {
                      id: "privacyPolicyConfirmed",
                      label: "I agree to the",
                      link: "/privacypolicy",
                      linkText: "Privacy Policy",
                    },
                    {
                      id: "warrantyConfirmed",
                      label:
                        "I agree to provide a 10-year warranty service, guaranteeing long-term quality and structural integrity for all construction projects that I build.",
                      link: null,
                      linkText: "",
                    },
                  ].map((agreement) => (
                    <div key={agreement.id} className="flex items-start">
                      <input
                        type="checkbox"
                        id={agreement.id}
                        checked={formData[agreement.id]}
                        onChange={() => handleCheckboxChange(agreement.id)}
                        className="mt-0.5 form-checkbox h-3 w-3 text-blue-600"
                        disabled={isSigningUp}
                      />
                      <label
                        htmlFor={agreement.id}
                        className="ml-2 text-xs text-gray-600"
                      >
                        {agreement.label}{" "}
                        {agreement.link && (
                          <a
                            href={agreement.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline font-medium"
                          >
                            {agreement.linkText}
                          </a>
                        )}
                        .<span className="text-red-500">*</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={handlePreviousStep}
              className={`px-5 py-2 rounded-lg text-xs ${currentStep === 0
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-500 text-white hover:bg-gray-600"
                } transition-colors`}
              disabled={currentStep === 0 || isSigningUp}
            >
              Back
            </button>
            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="bg-blue-500 text-white px-5 py-2 rounded-lg text-xs hover:bg-blue-600 transition-colors"
                disabled={isSigningUp}
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                className="bg-green-500 text-white px-5 py-2 rounded-lg text-xs hover:bg-green-600 flex items-center transition-colors"
                disabled={isSigningUp}
              >
                {isSigningUp ? (
                  <>
                    <AiOutlineLoading3Quarters className="animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  "Submit Registration"
                )}
              </button>
            )}
          </div>
        </form>
        <p className="mt-6 text-center text-xs text-gray-800">
          Already have an account?{" "}
          <span
            onClick={() => navigate("/login?type=builder")}
            className="text-blue-600 hover:underline cursor-pointer"
          >
            Login
          </span>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
